import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Container,
  Grid,
  Typography,
  Snackbar,
  Alert,
  TextField,
  Autocomplete,
} from "@mui/material";
import { format, parse } from "date-fns";
import { enGB } from "date-fns/locale";
import { formatDate } from "../utils/dateUtils";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import FormControl from "@mui/material/FormControl";
import { textFieldStyles } from "../styles/textFieldStyles";
import { useLocation } from "react-router-dom";
import CancelButton from "../components/CancelButton";
import SubmitButton from "../components/SubmitButton";
import { CircularProgress } from "@mui/material";

import BreadcrumbNav from "../components/BreadcrumbNav";
import FileAutocomplete from "../components/FileAutocomplete";
import { fetchFiles } from "../redux/actions/fileActions";
import { createDisbursement, getDisbursementDetails, updateDisbursement } from "../redux/actions/disbursementActions";
import { DISBURSEMENT_CREATE_RESET, DISBURSEMENT_UPDATE_RESET } from "../redux/constants/disbursementConstants";

// MUIDatePicker Component (no changes)
const MUIDatePicker = ({ value, onChange, sx, label }) => {
    const parsed =
      value instanceof Date
        ? value
        : value
          ? parse(value, "yyyy-MM-dd", new Date())
          : null;
  
    return (
      <FormControl variant="outlined" size="small" sx={sx}>
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
          <DatePicker
            label={label}
            value={parsed}
            onChange={(newDate) => {
              if (newDate) {
                onChange(format(newDate, 'yyyy-MM-dd'));
              } else {
                onChange('');
              }
            }}
            inputFormat="dd/MM/yyyy"
            mask="__/__/____"
            slotProps={{
              textField: {
                size: 'small',
                sx: {
                  '& .MuiOutlinedInput-root': { height: 33.53 },
                  '& .MuiOutlinedInput-input': { padding: '8px 14px' },
                  '& .MuiInputAdornment-root svg': { fontSize: '1.2rem' },
                },
              },
            }}
          />
        </LocalizationProvider>
      </FormControl>
    );
  };

const modalTableStyles = {
    fontFamily: '"Fira Sans", "Helvetica Neue", Helvetica, Roboto, Arial, sans-serif',
    headerCell: {
        backgroundColor: '#f5f5f5',
        color: '#333',
        fontWeight: 'bold',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        fontSize: '12px',
        padding: '10px 16px',
        fontFamily: 'inherit',
    },
    cell: {
        fontSize: '14px',
        padding: '12px 16px',
        fontFamily: 'inherit',
    }
};

const CreateDisbursementPage = () => {
    const { t } = useTranslation(['pages', 'forms', 'buttons', 'common']);
    const { id } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    
    console.log('CreateDisbursementPage component loaded');
    console.log('Location state:', location.state);

    const { files } = useSelector((state) => state.fileList);
    const { disbursement, loading } = useSelector((state) => state.disbursementDetails);
    const { loadingCreate, successCreate, errorCreate, disbursement: createdDisbursement } = useSelector((state) => state.disbursementCreate);
    const { loading: loadingUpdate, success: successUpdate, error: errorUpdate } = useSelector((state) => state.disbursementUpdate);
    const { user } = useSelector((state) => state.userLoaded);

    const [breadcrumbs, setBreadcrumbs] = useState([
        { label: t('navigation:home'), href: "/" },
        { label: t('pages:disbursements.title'), href: "/disbursements" },
    ]);
    
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarSeverity, setSnackbarSeverity] = useState("error");

    const [formData, setFormData] = useState(() => {
        const locationCaseId = location.state?.caseId;
        return {
            type: "",
            description: "",
            date: new Date(),
            case: locationCaseId || "",
            amount: "",
            invoiceNumber: "",
            owner: user?._id || "",
        };
    });

    useEffect(() => {
        dispatch(fetchFiles());
    }, [dispatch]);

    // Update breadcrumbs when files are loaded and we have a caseId
    useEffect(() => {
        const locationCaseId = location.state?.caseId;
        
        if (locationCaseId && files && files.length > 0) {
            const caseFile = files.find((file) => file._id === locationCaseId);
            if (caseFile) {
                setBreadcrumbs([
                    { label: "Files", href: "/files" },
                    {
                        label: caseFile.name || t('pages:files.case_file'),
                        href: `/files/${caseFile._id}/overview`,
                    },
                    { label: id ? t('pages:disbursements.edit') : t('pages:disbursements.create_new'), href: "" }
                ]);
            }
        }
    }, [files, location.state?.caseId, id, t]);

    useEffect(() => {
        if (id) {
            dispatch(getDisbursementDetails(id));
        }
    }, [dispatch, id]);

    useEffect(() => {
        if (disbursement && disbursement._id === id) {
            setFormData({
                type: disbursement.type || "",
                case: disbursement.case?._id || location.state?.caseId || "",
                date: disbursement.date ? new Date(disbursement.date) : new Date(),
                description: disbursement.description || "",
                amount: disbursement.amount || "",
                invoiceNumber: disbursement.invoiceNumber || "",
            });
        }
    }, [disbursement, id, location.state?.caseId]);

    useEffect(() => {
        if (user && !formData.owner) {
            setFormData(prev => ({
                ...prev,
                owner: user._id
            }));
        }
    }, [user, formData.owner]);

    useEffect(() => {
        if (successCreate) {
            setSnackbarMessage(t('pages:disbursements.created_successfully'));
            setSnackbarSeverity("success");
            setOpenSnackbar(true);
            
            // Store created disbursement ID if coming from invoice page
            const returnTo = location.state?.returnTo;
            if (returnTo && createdDisbursement?._id) {
                sessionStorage.setItem('lastCreatedDisbursementId', createdDisbursement._id);
                sessionStorage.setItem('disbursementCreationTimestamp', Date.now().toString());
            }
            
            setTimeout(() => {
                // Check if we should return to a specific page (e.g., invoice creation)
                const returnTo = location.state?.returnTo;
                if (returnTo) {
                    navigate(returnTo);
                } else {
                    // If coming from a file page, redirect back to file disbursements tab
                    const locationCaseId = location.state?.caseId;
                    if (locationCaseId) {
                        navigate(`/files/${locationCaseId}/disbursements`);
                    } else {
                        navigate("/disbursements");
                    }
                }
            }, 2000);
        }
        if (errorCreate) {
            setSnackbarMessage(errorCreate);
            setSnackbarSeverity("error");
            setOpenSnackbar(true);
        }
    }, [successCreate, errorCreate, navigate, location.state?.caseId, location.state?.returnTo, createdDisbursement, t]);

    useEffect(() => {
        if (successUpdate) {
            setSnackbarMessage(t('pages:disbursements.updated_successfully'));
            setSnackbarSeverity("success");
            setOpenSnackbar(true);
            
            setTimeout(() => {
                // If coming from a file page, redirect back to file disbursements tab
                const locationCaseId = location.state?.caseId;
                if (locationCaseId) {
                    navigate(`/files/${locationCaseId}/disbursements`);
                } else {
                    navigate("/disbursements");
                }
            }, 2000);
        }
        if (errorUpdate) {
            setSnackbarMessage(errorUpdate);
            setSnackbarSeverity("error");
            setOpenSnackbar(true);
        }
    }, [successUpdate, errorUpdate, navigate, location.state?.caseId, t]);

    // Reset the create and update state when component unmounts
    useEffect(() => {
        return () => {
            dispatch({ type: DISBURSEMENT_CREATE_RESET });
            dispatch({ type: DISBURSEMENT_UPDATE_RESET });
        };
    }, [dispatch]);

    const handleCancel = () => {
        // Check if we should return to a specific page (e.g., invoice creation)
        const returnTo = location.state?.returnTo;
        if (returnTo) {
            navigate(returnTo);
        } else {
            // If coming from a file page, redirect back to file disbursements tab
            const locationCaseId = location.state?.caseId;
            if (locationCaseId) {
                navigate(`/files/${locationCaseId}/disbursements`);
            } else {
                navigate(-1);
            }
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleCaseChange = (event, newValue) => {
        setFormData({ ...formData, case: newValue?._id || "" });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        
        if (!formData.type || !formData.date || !formData.amount || !formData.description || !formData.case) {
            setSnackbarMessage(t('forms:validation.required_fields'));
            setSnackbarSeverity("error");
            setOpenSnackbar(true);
            return;
        }
        
        if (!formData.owner && user) {
            setFormData(prev => ({
                ...prev,
                owner: user._id
            }));
        }
        
        try {
            console.log("Submitting form data:", formData);
            
            if (id) {
                dispatch(updateDisbursement(id, formData));
            } else {
                dispatch(createDisbursement(formData));
            }
        } catch (error) {
            setSnackbarMessage(error.message || t('common:error_occurred'));
            setSnackbarSeverity("error");
            setOpenSnackbar(true);
        }
    };
    
    const handleCloseSnackbar = () => {
        setOpenSnackbar(false);
    };

    return (
        <Container maxWidth="lg" style={{ position: "relative", paddingBottom: "80px" }}>
            <BreadcrumbNav
                title={id ? t('pages:disbursements.edit') : t('pages:disbursements.create_new')}
                breadcrumbs={breadcrumbs}
                showBreadcrumbs={true}
            />
            <Box sx={{ backgroundColor: "white", padding: "30px 24px", marginTop: "24px" }}>
                <form onSubmit={handleSubmit}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <FileAutocomplete
                                options={files}
                                value={files.find((file) => file._id === formData.case) || null}
                                onChange={handleCaseChange}
                                label="Case"
                                name="case"
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                             <TextField
                                label={t('common:invoice_number')}
                                name="invoiceNumber"
                                value={formData.invoiceNumber || ""}
                                onChange={handleChange}
                                fullWidth
                                size="small"
                                variant="outlined"
                                sx={textFieldStyles("100%")}
                                placeholder={t('forms:disbursement.invoice_number_placeholder')}
                             />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <Autocomplete
                                options={["Medisch advies", "Medische informatie", "Kadaster", "Handelsregister", "Deurwaarder", "Griffierechten", "Apostille", "Vertaalkoten", "Notaris", "Advieskosten derden", "Koeriersdiensten", "Publicatiekosten", "Porti", "Leges", "Overige verschotten"]}
                                getOptionLabel={(option) => option}
                                value={formData.type || null}
                                onChange={(event, newValue) => setFormData({ ...formData, type: newValue || "" })}
                                renderInput={(params) => (
                                    <TextField {...params} label="Type" fullWidth size="small" variant="outlined" sx={textFieldStyles("100%")} required />
                                )}
                            />
                        </Grid>
                        
                        <Grid item xs={12} sm={6}>
                            <MUIDatePicker
                                value={formData.date || null}
                                onChange={(newDate) => setFormData({ ...formData, date: newDate })}
                                label={t('forms:labels.date')}
                                sx={textFieldStyles("100%")}
                                required
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                label={t('forms:labels.amount')}
                                name="amount"
                                type="number"
                                variant="outlined"
                                value={formData.amount}
                                onChange={handleChange}
                                fullWidth
                                required
                                size="small"
                                sx={textFieldStyles("100%")}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                label={t('forms:labels.description')}
                                name="description"
                                value={formData.description}
                                onChange={handleChange}
                                fullWidth
                                multiline
                                rows={4}
                                required
                                size="small"
                                variant="outlined"
                                sx={textFieldStyles("100%")}
                            />
                        </Grid>
                    </Grid>

                    <Box sx={{ position: "fixed", bottom: 0, left: 0, right: 0, display: "flex", justifyContent: "flex-end", gap: 1, zIndex: 10, backgroundColor: "white", padding: "13px 80px" }}>
                        <CancelButton variant="outlined" onClick={handleCancel} size="small">{t('buttons:secondary.cancel')}</CancelButton>
                        <SubmitButton type="submit" size="small">
                            {(loadingCreate || loading || loadingUpdate) ? <CircularProgress size={24} sx={{ color: "white" }} /> : (id ? t('buttons:save') : t('pages:disbursements.create_new'))}
                        </SubmitButton>
                    </Box>
                </form>
            </Box>

            <Snackbar open={openSnackbar} autoHideDuration={6000} onClose={handleCloseSnackbar}>
                <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default CreateDisbursementPage;